/**
 * 현장 개요의 '운영 프로세스' 위젯 (view-only).
 *
 * 좁은 카드 안에서 진행 상황을 한눈에 보여주는 데 집중한다:
 *   1) 진행률 (done / total · pct%) + 막대
 *   2) 현재 진행해야 할 단계 (강조 카드) + '완료' 버튼
 *      → 완료 누르면 해당 단계의 checked = true 로 갱신되고
 *         자동으로 다음 단계가 같은 위치에 노출됨 (mutation → query refetch).
 *   3) 다음 단계 1-2개 미리보기.
 *   4) '전체 보기' 링크 → 단계 추가/삭제/세부 편집은
 *      /project/:id/operation-process 페이지의 워크플로 뷰에서 수행한다.
 *
 * 스타일은 운영 템플릿/현장 운영 프로세스와 같은 업무절차 워크플로 토큰
 * (workflowTokens)을 쓴다 — 같은 데이터를 좁은 카드로 줄인 것이므로 개요만
 * 다른 팔레트로 보이면 안 된다.
 *
 * 데이터:
 *   - `useQuery(['operation-process','by-site', siteId])` — 메인 대시보드의
 *     `OperationProgressBadge` 와 동일 키 → 한쪽에서 invalidate 하면 양쪽 갱신.
 *   - mutation 은 부분 PUT 이 없으므로 전체 OperationProcessData 를 다시 저장.
 */

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForwardIos as ArrowIcon,
  Check as CheckIcon,
  PlaylistAddCheck as ProcessIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import WidgetCard from '../WidgetCard';
import { useWorkflowTokens } from '../../../components/operation-process/WorkflowStepCard';
import { WfTokens } from '../../../components/operation-process/operationWorkflowModel';
import {
  OperationProcessData,
  OperationProcessService,
  computeOperationProgress,
} from '../../../services/OperationProcessService';

interface OperationProcessWidgetProps {
  projectId: number;
}

/** 부모/자식 행을 평탄화한 단일 단계 식별자. UI 가 다루기 좋도록 derived. */
interface FlatStep {
  rowId: string;
  /** 자식 단계면 자식 id, 부모 단계면 undefined. */
  childId?: string;
  category: string;
  name: string;
  checked: boolean;
  isParent: boolean;
}

function flattenSteps(data: OperationProcessData | null | undefined): FlatStep[] {
  if (!data?.rows?.length) return [];
  const result: FlatStep[] = [];
  data.rows.forEach((r, idx) => {
    const baseName = (r.name || '').trim() || `${r.category || '항목'} #${idx + 1}`;
    result.push({
      rowId: r.id,
      category: r.category || '',
      name: baseName,
      checked: !!r.checked,
      isParent: true,
    });
    (r.children ?? []).forEach((c, cIdx) => {
      const cName = (c.name || '').trim() || `${baseName} 하위 #${cIdx + 1}`;
      result.push({
        rowId: r.id,
        childId: c.id,
        category: r.category || '',
        name: cName,
        checked: !!c.checked,
        isParent: false,
      });
    });
  });
  return result;
}

function markStepDone(data: OperationProcessData, step: FlatStep): OperationProcessData {
  return {
    ...data,
    rows: data.rows.map((r) => {
      if (r.id !== step.rowId) return r;
      if (step.isParent) return { ...r, checked: true };
      return {
        ...r,
        children: (r.children ?? []).map((c) =>
          c.id !== step.childId ? c : { ...c, checked: true },
        ),
      };
    }),
  };
}

const OperationProcessWidget: React.FC<OperationProcessWidgetProps> = ({ projectId }) => {
  const wf = useWorkflowTokens();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const enabled = Number.isFinite(projectId) && projectId > 0;

  const queryKey = ['operation-process', 'by-site', projectId] as const;
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => OperationProcessService.getBySite(projectId),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (next: OperationProcessData) =>
      OperationProcessService.updateBySite(projectId, next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<OperationProcessData>(queryKey);
      queryClient.setQueryData(queryKey, next);
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const progress = useMemo(() => computeOperationProgress(data ?? null), [data]);
  const flat = useMemo(() => flattenSteps(data ?? null), [data]);
  const pending = useMemo(() => flat.filter((s) => !s.checked), [flat]);
  const current = pending[0] ?? null;
  const upcoming = pending.slice(1, 3);

  const handleComplete = (step: FlatStep): void => {
    if (!data || mutation.isPending) return;
    mutation.mutate(markStepDone(data, step));
  };

  const handleOpenPage = (): void => {
    if (!enabled) return;
    navigate(`/project/${projectId}/operation-process`);
  };

  return (
    <WidgetCard
      title="운영 프로세스"
      icon={<ProcessIcon fontSize="small" />}
      actionLabel="전체 보기"
      onAction={handleOpenPage}
      testId="widget-operation-process"
    >
      {!enabled ? (
        <Typography variant="caption" color="text.secondary">
          유효한 현장 ID 가 없습니다.
        </Typography>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : isError ? (
        <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
          운영 프로세스를 불러오지 못했습니다.
        </Alert>
      ) : (
        <Stack spacing={1.25}>
          {/* 진행률 */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: wf.font,
                fontWeight: 800,
                color: wf.acc,
                fontVariantNumeric: 'tabular-nums',
              }}
              data-testid="op-progress-pct"
            >
              {progress.pct}%
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: wf.font,
                color: wf.muted,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              ({progress.done}/{progress.total})
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress.pct}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: wf.track,
              '& .MuiLinearProgress-bar': { bgcolor: wf.acc, borderRadius: 3 },
            }}
          />

          {/* 현재 단계 카드 */}
          {progress.total === 0 ? (
            <Box sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontFamily: wf.font, color: wf.muted }}>
                아직 등록된 단계가 없습니다. '전체 보기'에서 단계를 추가하세요.
              </Typography>
            </Box>
          ) : current ? (
            <CurrentStepCard
              step={current}
              wf={wf}
              busy={mutation.isPending}
              onComplete={() => handleComplete(current)}
            />
          ) : (
            <Box
              sx={{
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                bgcolor: wf.accPale,
                border: `1px solid ${wf.acc}`,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <CheckIcon fontSize="small" sx={{ color: wf.accDeep }} />
              <Typography
                variant="body2"
                sx={{ fontFamily: wf.font, fontWeight: 700, color: wf.accDeep }}
              >
                모든 단계 완료
              </Typography>
            </Box>
          )}

          {/* 다음 단계 미리보기 */}
          {upcoming.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontFamily: wf.font, fontWeight: 700, color: wf.muted }}
              >
                다음 단계
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {upcoming.map((step) => (
                  <Stack
                    key={step.childId ?? step.rowId}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ minWidth: 0 }}
                  >
                    <ArrowIcon sx={{ fontSize: 10, color: wf.ghost }} />
                    {step.category && <CategoryTag label={step.category} wf={wf} />}
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: wf.font,
                        color: wf.body,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.name}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {mutation.isError && (
            <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
              완료 처리에 실패했습니다. 다시 시도해주세요.
            </Alert>
          )}
        </Stack>
      )}
    </WidgetCard>
  );
};

/** 업무절차 카드의 카테고리 배지와 같은 모양. */
const CategoryTag: React.FC<{ label: string; wf: WfTokens; onInverse?: boolean }> = ({
  label,
  wf,
  onInverse,
}) => (
  <Box
    component="span"
    sx={{
      flexShrink: 0,
      fontFamily: wf.font,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.3,
      px: '6px',
      py: '2px',
      borderRadius: '4px',
      bgcolor: onInverse ? 'transparent' : wf.chipBg,
      border: `1px solid ${onInverse ? wf.inverseLabel : wf.line}`,
      color: onInverse ? wf.inverseLabel : wf.subInk,
    }}
  >
    {label}
  </Box>
);

interface CurrentStepCardProps {
  step: FlatStep;
  wf: WfTokens;
  busy: boolean;
  onComplete: () => void;
}

/** 진행 중 단계 — 업무절차 뷰와 동일하게 반전(inverse) 표면으로 강조한다. */
const CurrentStepCard: React.FC<CurrentStepCardProps> = ({ step, wf, busy, onComplete }) => {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: '8px',
        bgcolor: wf.inverseBg,
        color: wf.inverseFg,
      }}
      data-testid="op-current-step"
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: wf.font,
            fontWeight: 800,
            color: wf.inverseLabel,
            letterSpacing: 0.6,
            fontSize: '0.62rem',
          }}
        >
          현재 단계
        </Typography>
        {step.category && <CategoryTag label={step.category} wf={wf} onInverse />}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: wf.font,
            fontWeight: 700,
            color: wf.inverseFg,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={step.name}
        >
          {step.name}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={onComplete}
          disabled={busy}
          disableElevation
          data-testid="op-complete-btn"
          sx={{
            bgcolor: wf.acc,
            color: '#fff',
            fontFamily: wf.font,
            textTransform: 'none',
            fontWeight: 700,
            px: 1.25,
            py: 0.25,
            minHeight: 28,
            borderRadius: '6px',
            '&:hover': { bgcolor: wf.accDeep },
          }}
        >
          완료
        </Button>
      </Stack>
    </Box>
  );
};

export default OperationProcessWidget;
